import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay, Pagination } from 'swiper/modules'
import type { Swiper as SwiperType } from 'swiper'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Container } from '@/components/layout/Container'
import { AnimateIn } from '@/components/motion/AnimateIn'
import type { FreshProjectSlide, FreshProjectSlider } from '@/types/home'
import {
  SLIDER_EDGE_INNER,
  SLIDER_NAV_ICON_STROKE,
  sliderNavIconClass,
  sliderNavNextClass,
  sliderNavPrevClass,
} from '@/lib/sliderNavStyles'
import { normalizeSliderHref } from '@/lib/sliderLink'
import { trackHomepageBannerClick } from '@/lib/analytics/bannerEvents'
import { OptimizedImage } from '@/components/ui/OptimizedImage'
import { cn } from '@/lib/utils'
import { SECTION_PY } from '@/lib/layoutSpacing'

function SlideLink({
  href,
  onOpen,
  children,
}: {
  href: string
  onOpen?: (url: string) => void
  children: ReactNode
}) {
  const url = normalizeSliderHref(href)

  if (!url) {
    return <div className="block">{children}</div>
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => onOpen?.(url)}
      className="block cursor-pointer transition-opacity hover:opacity-95"
      aria-label="Open project link in new tab"
    >
      {children}
    </a>
  )
}

interface FreshProjectsProps {
  slides?: FreshProjectSlide[]
  /** @deprecated Use `slides` — kept for grouped API responses */
  sliders?: FreshProjectSlider[]
}

export function FreshProjects({ slides, sliders }: FreshProjectsProps) {
  const allSlides = useMemo(() => {
    if (slides?.length) return slides
    return sliders?.flatMap((s) => s.slides) ?? []
  }, [slides, sliders])

  const [swiper, setSwiper] = useState<SwiperType | null>(null)
  const canLoop = allSlides.length > 1

  const navIcon = sliderNavIconClass('md')

  if (allSlides.length === 0) return null

  return (
    <Container>
      <AnimateIn>
        <section className={SECTION_PY}>
          <div className="mb-2 text-center md:mb-5">
            <h2 className="font-display text-[1.35rem] font-bold leading-tight md:text-[1.75rem] lg:text-3xl">
              <span className="text-primary-600">Fresh</span>
              <span className="text-neutral-900"> Projects</span>
            </h2>
          </div>

          <div className="overflow-visible pl-0 pr-0 md:pr-[22px]">
            <div className={SLIDER_EDGE_INNER}>
              <Swiper
                modules={[Autoplay, Pagination]}
                loop={canLoop}
                autoplay={canLoop ? { delay: 4500, disableOnInteraction: false } : false}
                pagination={{ clickable: true }}
                onSwiper={setSwiper}
                className="fresh-projects-swiper overflow-hidden rounded-xl border border-neutral-200 bg-neutral-50 shadow-[0_4px_24px_rgba(15,23,42,0.08)] md:rounded-2xl"
              >
                {allSlides.map((slide, index) => (
                  <SwiperSlide key={slide.id} className="h-auto! self-center">
                    <SlideLink
                      href={slide.href}
                      onOpen={(url) => trackHomepageBannerClick(slide.id, index, url)}
                    >
                      <OptimizedImage
                        src={slide.image}
                        alt={slide.alt}
                        className={cn(
                          'block h-auto w-full max-w-full',
                          slide.href && 'cursor-pointer',
                        )}
                        priority={false}
                      />
                    </SlideLink>
                  </SwiperSlide>
                ))}
              </Swiper>

              {canLoop && (
                <>
                  <button
                    type="button"
                    aria-label="Previous banner"
                    onClick={() => swiper?.slidePrev()}
                    className={cn(sliderNavPrevClass('md', 'center'), 'hidden md:flex')}
                  >
                    <ChevronLeft className={navIcon} strokeWidth={SLIDER_NAV_ICON_STROKE} />
                  </button>
                  <button
                    type="button"
                    aria-label="Next banner"
                    onClick={() => swiper?.slideNext()}
                    className={cn(sliderNavNextClass('md', 'center'), 'hidden md:flex')}
                  >
                    <ChevronRight className={navIcon} strokeWidth={SLIDER_NAV_ICON_STROKE} />
                  </button>
                </>
              )}
            </div>
          </div>
        </section>
      </AnimateIn>
    </Container>
  )
}
